import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import lotus from 'assets/themes/cultureDay/header_right.svg';
import lotusImage from 'assets/themes/cultureDay/footer_right.svg';
import { isDashboardRoute } from './isDashboardRoute';
const EFFECT_Z_INDEX = 11;

/** Tông hồng 20/10 — cánh hoa & mảnh pháo giấy */
const CONFETTI_COLORS = [
    '#ffb3c6',
    '#ff8fab',
    '#ffc2d1',
    '#ffe5ec',
    '#f9a8c9',
    '#ffffff',
    '#fda4bf',
    '#fecdd3',
];

const GRAVITY = 320;
const BURST_INTERVAL_MS = 4200;
const BURST_PIECE_COUNT = 38;
const MAX_PIECES = 220;

function random(min, max) {
    return min + Math.random() * (max - min);
}

function pickColor() {
    return CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
}

/** Pháo giấy bắn từ một điểm (góc dưới / cạnh màn hình) */
function createBurstPieces(originX, originY, count, spreadBias = 0) {
    const pieces = [];
    for (let i = 0; i < count; i += 1) {
        const angle = random(-Math.PI * 0.92, -Math.PI * 0.08) + spreadBias;
        const speed = random(220, 520);
        pieces.push({
            x: originX + random(-8, 8),
            y: originY + random(-6, 6),
            w: random(6, 14),
            h: random(4, 11),
            vx: Math.cos(angle) * speed + random(-40, 40),
            vy: Math.sin(angle) * speed,
            rotation: random(0, Math.PI * 2),
            spin: random(-4.5, 4.5),
            swayPhase: random(0, Math.PI * 2),
            swaySpeed: random(28, 62),
            color: pickColor(),
            shape: Math.random() > 0.32 ? 'petal' : 'paper',
            opacity: random(0.65, 1),
        });
    }
    return pieces;
}

function drawConfettiPiece(ctx, piece) {
    ctx.save();
    ctx.translate(piece.x, piece.y);
    ctx.rotate(piece.rotation);
    ctx.globalAlpha = piece.opacity;
    ctx.fillStyle = piece.color;

    if (piece.shape === 'petal') {
        ctx.beginPath();
        ctx.ellipse(0, 0, piece.w * 0.52, piece.h * 0.38, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = piece.opacity * 0.45;
        ctx.beginPath();
        ctx.ellipse(piece.w * 0.08, 0, piece.w * 0.22, piece.h * 0.18, 0.35, 0, Math.PI * 2);
        ctx.fill();
    } else {
        ctx.fillRect(-piece.w / 2, -piece.h / 2, piece.w, piece.h);
    }

    ctx.restore();
}

function WomenDayConfetti() {
    const canvasRef = useRef(null);
    const piecesRef = useRef([]);
    const rafRef = useRef(0);
    const burstTimerRef = useRef(0);
    const burstSideRef = useRef(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return undefined;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return undefined;
        }

        const resize = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const width = window.innerWidth;
            const height = window.innerHeight;

            canvas.width = width * dpr;
            canvas.height = height * dpr;
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };

        resize();
        window.addEventListener('resize', resize);

        const width = () => window.innerWidth;
        const height = () => window.innerHeight;

        piecesRef.current = [];

        const fireBurst = () => {
            const w = width();
            const h = height();
            const side = burstSideRef.current % 3;
            burstSideRef.current += 1;

            let origins;
            if (side === 0) {
                origins = [{ x: w * 0.06, y: h * 0.88, bias: 0.15 }];
            } else if (side === 1) {
                origins = [{ x: w * 0.94, y: h * 0.88, bias: -0.15 }];
            } else {
                origins = [
                    { x: w * 0.22, y: h * 0.9, bias: 0.08 },
                    { x: w * 0.78, y: h * 0.9, bias: -0.08 },
                ];
            }

            origins.forEach(({ x, y, bias }) => {
                const burst = createBurstPieces(x, y, BURST_PIECE_COUNT, bias);
                piecesRef.current.push(...burst);
            });

            if (piecesRef.current.length > MAX_PIECES) {
                piecesRef.current = piecesRef.current.slice(-MAX_PIECES);
            }
        };

        fireBurst();
        burstTimerRef.current = window.setInterval(fireBurst, BURST_INTERVAL_MS);

        let lastTime = performance.now();

        const draw = (now) => {
            const dt = Math.min((now - lastTime) / 1000, 0.04);
            lastTime = now;

            const w = width();
            const h = height();

            ctx.clearRect(0, 0, w, h);

            const next = [];

            for (const piece of piecesRef.current) {
                piece.x += (piece.vx + Math.sin(now * 0.0018 + piece.swayPhase) * piece.swaySpeed) * dt;
                piece.y += piece.vy * dt;
                piece.vy += GRAVITY * dt;
                piece.rotation += piece.spin * dt;

                const drag = Math.pow(0.985, dt * 60);
                piece.vx *= drag;

                if (piece.y > h + 24 || piece.x < -40 || piece.x > w + 40) {
                    continue;
                }

                drawConfettiPiece(ctx, piece);
                next.push(piece);
            }

            piecesRef.current = next;

            rafRef.current = window.requestAnimationFrame(draw);
        };

        rafRef.current = window.requestAnimationFrame(draw);

        return () => {
            window.removeEventListener('resize', resize);
            window.clearInterval(burstTimerRef.current);
            window.cancelAnimationFrame(rafRef.current);
            piecesRef.current = [];
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            aria-hidden="true"
            style={{
                position: 'fixed',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: EFFECT_Z_INDEX,
            }}
        />
    );
}

export default function CultureDayEffect() {
    const { pathname } = useLocation();
    const showConfetti = isDashboardRoute(pathname);

    return (
        <>
            <img
                src={lotus}
                alt=""
                className="culture-day-header-banner"
            />
            <div className="theme-effects-layer" aria-hidden>
                {/* {showConfetti ? <WomenDayConfetti /> : null} */}
                <img
                    src={lotusImage}
                    alt=""
                    className="national-day-footer-pin"
                    style={{
                        position: 'fixed',
                        right: 0,
                        bottom: 0,
                        width: 200,
                        height: 'auto',
                        zIndex: EFFECT_Z_INDEX,
                        pointerEvents: 'none',
                    }}
                />
            </div>
        </>
    );
}
